/**
 * Which tenant are we looking at? AUTH-01.
 *
 * Four rungs, in order, and the order is the whole design:
 *
 *   1. **The hostname**, on a per-company address:
 *      `acme.hr.linkco.jo` → "acme", `acme.linkhr.test` → "acme".
 *   2. **The link**, via `?tenantSlug=` — how invite and password-reset emails
 *      name the company on the shared host, for a person with no session yet.
 *   3. **The session**, on an address that names no company. The global entry
 *      host `app.[staging.]hr.linkco.jo` serves the SAME apps for EVERY
 *      company, so the hostname cannot answer and the login response's
 *      `tenantSlug` (recorded by `performLogin` in `sessionTenantSlugStore`)
 *      is the only honest source.
 *   4. **The dev fallback**, on a local host only (`localhost`, loopback,
 *      bare `linkhr.test`).
 *
 * Hosts that name NO tenant:
 *   `app.*`                — the global entry host. `app` is a backend
 *                            reserved slug precisely so it can never be one.
 *   `hr.linkco.jo`, `staging.hr.linkco.jo`, `linkhr.test`, `linkco.jo`
 *                          — apex / no-tenant-subdomain roots; the first label
 *                            ("hr", "staging", "linkhr") is not a slug.
 *   `localhost` / loopback — local development.
 *
 * Rung 4 is DELIBERATELY gated to local hosts. `VITE_DEV_TENANT_SLUG` defaults
 * to `"acme"`, so an ungated fallback signs every visitor of the global host
 * into the demo company — a confident wrong answer, which is worse than none.
 * Off a dev host with nothing to go on, `resolveTenant` returns `""` and the
 * app shells send the user to the global login rather than guess.
 *
 * `resolveTenantOrNull` is the same resolution without rung 3 — the callers
 * that need to DECIDE ("do we know the company at all?") use it; the ~330
 * callers that just need a slug for a query keep the `string` API.
 *
 * The env fallback is injected via `fallbackSlug` rather than read from
 * `import.meta.env` so `@linkhr/auth` stays Vite-free + trivially testable.
 */
import { sessionTenantSlugStore } from "../tokens/sessionTenantSlugStore.js";

/** Local development hosts — the only place the build-time fallback applies. */
const DEV_HOST_MATCHERS = /^(localhost|127(?:\.\d+){0,3}|0\.0\.0\.0|linkhr\.test)$/;

/**
 * The global entry host, on any root (`app.hr.linkco.jo`,
 * `app.staging.hr.linkco.jo`, `app.linkhr.test`, `app.localhost`). Never a
 * tenant: the backend's `ReservedSlugs` reserves `app` for exactly this.
 */
const GLOBAL_HOST_MATCHERS = /^app\./;

/** Apex / no-tenant-subdomain hosts. A tenant is always `{slug}.{apex}`. */
const ROOT_HOST_MATCHERS = /^(hr\.linkco\.jo|staging\.hr\.linkco\.jo|linkco\.jo)$/;

export interface ResolveTenantOpts {
  /** Slug to return when the hostname is a LOCAL dev host with no session. */
  fallbackSlug: string;
  /** Optional explicit hostname — useful for tests; defaults to `window.location.hostname`. */
  hostname?: string;
  /** Optional explicit query string — useful for tests; defaults to `window.location.search`. */
  search?: string;
}

function currentHostname(hostname?: string): string {
  return hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
}

/**
 * Local development, where the build-time fallback is legitimate. A
 * single-label host ("linkhr", a LAN machine name) counts: a public tenant
 * address always has a dot. `app.localhost` does NOT — it is a global host.
 */
function isDevHost(host: string): boolean {
  if (GLOBAL_HOST_MATCHERS.test(host)) return false;
  return host === "" || DEV_HOST_MATCHERS.test(host) || !host.includes(".");
}

/** Rung 1 — the slug in the address, or `null` when the address has none. */
function hostTenantSlug(host: string): string | null {
  if (GLOBAL_HOST_MATCHERS.test(host) || isDevHost(host) || ROOT_HOST_MATCHERS.test(host)) {
    return null;
  }
  const slug = host.split(".")[0];
  return slug !== undefined && slug.length > 0 ? slug : null;
}

/** True on the global entry host, where the address names no company by design. */
export function isGlobalEntryHost(hostname?: string): boolean {
  return GLOBAL_HOST_MATCHERS.test(currentHostname(hostname));
}

/**
 * The tenant slug from the address or the session, or `null` when neither
 * knows. Callers that must DECIDE something (render a tenant login vs. bounce
 * to the global one) use this; callers that just need a slug use
 * {@link resolveTenant}.
 */
export function resolveTenantOrNull(
  opts: { hostname?: string; search?: string } = {},
): string | null {
  const host = currentHostname(opts.hostname);
  return hostTenantSlug(host) ?? linkTenantSlug(opts.search) ?? sessionTenantSlugStore.get();
}

export function resolveTenant({ fallbackSlug, hostname, search }: ResolveTenantOpts): string {
  const host = currentHostname(hostname);
  const resolved = hostTenantSlug(host) ?? linkTenantSlug(search) ?? sessionTenantSlugStore.get();
  if (resolved !== null) return resolved;
  // Dev hosts only — see the header note on why this is gated.
  return isDevHost(host) ? fallbackSlug : "";
}

/** Backend slug grammar — the same rule the session store applies. */
const LINK_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/;

/**
 * Rung 2 — the company a link names, via `?tenantSlug=`.
 *
 * On the shared host, invite and password-reset emails carry the company in the
 * query string: the person opening them has no session yet, so without this the
 * app finds no company and sends them to the global sign-in, dropping the token.
 * The slug here only labels the visit (branding, cache keys); the backend still
 * authorises by the invite or reset token, so reading it from the URL grants
 * nothing.
 *
 * It is written into the session store when that store is empty, because the
 * router drops the query string on the first navigation and the rest of the
 * visit (accept → set password → sign in) must keep the same company. It never
 * overwrites a slug a live session already holds.
 */
function linkTenantSlug(search?: string): string | null {
  const query = search ?? (typeof window !== "undefined" ? window.location.search : "");
  if (query === "") return null;
  const slug = new URLSearchParams(query).get("tenantSlug");
  if (slug === null || !LINK_SLUG.test(slug)) return null;
  if (sessionTenantSlugStore.get() === null) sessionTenantSlugStore.set(slug);
  return slug;
}
